// The improved version of the My Intrivity Application
import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core"; 
import { getFunctions, httpsCallable } from "firebase/functions";
import { functions } from './intrivity_app.js';
 
import { 
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS,
    INTRIVITY_LOAD_TRAINER, INTRIVITY_LOAD_PROGRAM_MANAGER, INTRIVITY_LOAD_CLIENT_MANAGER,
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN, 
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN, 
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE, 
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE 
} from './utility.js'
import './badge.js';
import { a11yStyles, announce } from './a11y.js';
import { t, Localized } from './i18n.js';

export class IntrivityNavApp extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .nav {
                /* Darkened from rgb(87,130,133) so white body text clears the
                   WCAG AA 4.5:1 threshold (was 4.25:1, now 4.8:1). */
                background-color: rgb(80, 120, 123);
                color: white;
                height: 100%;
                font-size: 24px;
                min-height: 600px;
            }
            .nav.mobile {
                min-height: 0;
                height: auto;
            }
            ul.menu {
                list-style: none;
                margin: 0;
                padding: 0;
            }
            .selection {
                cursor: pointer;
                font-size: 1.3vmax;
                padding-top: 5px;
                padding-bottom: 5px;
                padding-left: 20px;
                padding-right: 5px;
                display: block;
                width: 100%;
                box-sizing: border-box;
            }
            /* 10px further in than the section label above them. */
            .domain-link { padding-left: 30px; }
            .domain-link .first-letter {
                font-weight: 900;
                font-size: 1.2em;
            }
            .nav-icon {
                width: 1.2em;
                height: 1.2em;
                margin-right: 8px;
                vertical-align: -0.25em;
            }
            .selected {
                font-size: 1.3vmax;
                /* Dark teal on the green highlight is 6.6:1; the original
                   rgb(87,130,133) on this green was only 2.5:1. */
                color: rgb(30, 64, 66);
                background-color: rgb(175, 208, 117);
                font-weight: 600;
            }
            /* The Space Domain icon is the same green as this highlight, so
               without an outline it disappears into the background when selected. */
            .selected .nav-icon {
                filter:
                    drop-shadow(1px 0 0 rgb(30, 64, 66))
                    drop-shadow(-1px 0 0 rgb(30, 64, 66))
                    drop-shadow(0 1px 0 rgb(30, 64, 66))
                    drop-shadow(0 -1px 0 rgb(30, 64, 66));
            }
            /* Padding, not margin: a top margin on the first item would
               collapse up through the <ul> and push the whole teal panel down,
               leaving a white gap under the header. */
            .gap-none { padding-top: 0; }
            .gap-xs { padding-top: 10px; }
            .gap-sm { padding-top: 30px; }
            .gap-lg { padding-top: 80px; }
            .gap-xl { padding-top: 100px; }
            .mobile-menu .gap-xs,
            .mobile-menu .gap-sm,
            .mobile-menu .gap-lg,
            .mobile-menu .gap-xl { padding-top: 0; }
            .section-label {
                font-size: 0.85vmax;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                padding-left: 20px;
                padding-right: 5px;
                box-sizing: border-box;
                opacity: 0.85;
            }
            .mobile-menu .section-label {
                font-size: 0.7em;
                padding: 12px 20px 0;
            }
            .common {
                background-color: rgb(250, 166, 53); // ORANGE
                background-color: rgb(82, 162, 170); // TEAL
                background-color: rgb(175, 208, 117); // GREEN
                background-color: rgb(217, 90, 123); // PINK
                background-color: rgb(143, 143, 143); // GRAY
            }
            .mobile-bar {
                display: flex;
                align-items: center;
                padding: 12px 20px;
                font-size: 1.3em;
                cursor: pointer;
                width: 100%;
            }
            /* Reuses the same menu markup/handlers as the desktop sidebar,
               just re-skinned for a compact dropdown under the mobile bar. */
            .mobile-menu {
                border-top: 1px solid rgba(255,255,255,0.3);
                padding-bottom: 20px;
            }
            .mobile-menu .selection {
                font-size: 0.77em;
                padding: 12px 20px;
            }
            /* 10px further in than the mobile section label above them. */
            .mobile-menu .domain-link {
                padding-left: 30px;
            }
            .roles-guide,
            .accessibility-link,
            .privacy-link {
                font-size: 0.6vmax;
            }
            .mobile-menu .roles-guide,
            .mobile-menu .accessibility-link,
            .mobile-menu .privacy-link {
                font-size: 0.53em;
            }
            /* The focus ring needs to read against both the teal sidebar and
               the green "selected" highlight. */
            .nav button:focus-visible {
                outline: 3px solid #ffffff;
                outline-offset: -3px;
                box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.55);
            }
        `];
    }

    static get properties() {
        return {
            masqueraded_user: { type: String },
            selection: { type: String },
            user_profile: { type: Object },     // user data
            report: { type: Object },
            horizontal: { type: Boolean },
            group_reports: { type: Array },
            _mobileMenuOpen: { type: Boolean }
        };
    }

    async connectToMoodle() {
      try {
        // call the callable createSsoJwt
        const createSso = httpsCallable(functions, 'createSsoJwt');
        const res = await createSso({}); // no data needed; server uses auth context
        if (!res || !res.data || !res.data.token) {
          throw new Error('SSO function did not return token');
        }
        const ssoToken = res.data.token;
        console.log(ssoToken);
        const redirectUrl = res.data.redirectUrl || 'https://moodle.intrivity.com/local/sso/receive.php';

        // Post the token to Moodle via an auto-submitting form (keeps token out of URL)
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = redirectUrl;
        // it's important the Moodle endpoint accepts POST with field name 'sso_token' (or whichever you configure)
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'sso_token';
        input.value = ssoToken;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();

      } catch (err) {
        console.error('Failed to create SSO token', err);
        alert(t('nav.moodleError', { message: err.message || err.toString() }));
      }
    }

    handleSelection(e) {
        if (e.detail.selection !== this.selection) {
            console.log(e);
            this.selection = e.detail.selection;
            this.requestUpdate();
        }
    }
    
    constructor() {
        super();
        this.selection = "overview";
        this.handelSelection = this.handleSelection.bind(this);
        this.connectToMoodle = this.connectToMoodle.bind(this);
        this._mobileMenuOpen = false;
        //this.horizontal = false;
        //this.selection = "overview";
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(INTRIVITY_NAV_SELECT, this.handleSelection); 
    }
    
    disconnectedCallback() {
        this.removeEventListener(INTRIVITY_NAV_SELECT, this.handleSelection);
        super.disconnectedCallback();
    }
    
    // Shared by the desktop sidebar and the mobile dropdown — same items,
    // same click handlers, just re-skinned by the .mobile-menu CSS scope
    // when rendered inside the mobile dropdown.
    //
    // Each item is a real <button> inside a list item: keyboard-operable,
    // exposed with an accessible name, and marked aria-current="page" when it
    // is the view currently showing in <main>.
    // Bolds/enlarges the first letter of the domain's name within its label,
    // e.g. "Message Domain" -> "𝐌essage Domain", "Domaine Message" -> "Domaine 𝐌essage".
    // The full label is still what's announced to assistive tech.
    _emphasizeFirstLetter(label, name) {
        const at = Math.max(0, label.indexOf(name));
        return html`${label.slice(0, at)}<span class="first-letter">${label.charAt(at)}</span>${label.slice(at + 1)}`;
    }

    _navItem(selection, label, { gap = 'gap-sm', extraClass = '', srLabel, icon, onSelect } = {}) {
        const isCurrent = this.selection === selection;
        return html`
            <li class="${gap}">
                <button
                    type="button"
                    class="unstyled-button selection ${extraClass} ${isCurrent ? 'selected' : ''}"
                    aria-current=${isCurrent ? 'page' : 'false'}
                    @click=${(e) => {
                        if (onSelect) {
                            onSelect(e);
                        } else {
                            emitEvent(INTRIVITY_NAV_SELECT, { "selection": selection }, this);
                        }
                        announce(t('nav.selected', { label: srLabel || label }));
                    }}>${icon ? html`<img class="nav-icon" src="./images/${icon}" alt="" aria-hidden="true" />` : html``}${label}</button>
            </li>
        `;
    }

    _renderMenuContent() {
        const paidWithReport = this.report && this.user_profile && this.user_profile.paid;
        const isStaff = hasRole('admin', this.user_profile) || hasRole('trainer', this.user_profile)
            || hasRole('program_manager', this.user_profile) || hasRole('client_manager', this.user_profile);

        return html`
            <ul class="menu">
                ${this._navItem('overview', t('nav.overview'), { gap: 'gap-sm' })}

                ${this.user_profile ? html`

                    ${paidWithReport ? this._navItem('profile', t('nav.profile')) : html``}

                    ${paidWithReport ? html`<li class="gap-sm section-label" aria-hidden="true">${t('nav.detailedResults')}</li>` : html``}
                    ${paidWithReport ? this._navItem('message', this._emphasizeFirstLetter(t('domain.message.label'), t('domain.message.name')), { gap: 'gap-xs', extraClass: 'domain-link', icon: 'message-icon.png', srLabel: t('domain.message.label') }) : html``}
                    ${paidWithReport ? this._navItem('relationship', this._emphasizeFirstLetter(t('domain.relationship.label'), t('domain.relationship.name')), { gap: 'gap-xs', extraClass: 'domain-link', icon: 'relationship-icon.png', srLabel: t('domain.relationship.label') }) : html``}
                    ${paidWithReport ? this._navItem('time', this._emphasizeFirstLetter(t('domain.time.label'), t('domain.time.name')), { gap: 'gap-xs', extraClass: 'domain-link', icon: 'time-icon.png', srLabel: t('domain.time.label') }) : html``}
                    ${paidWithReport ? this._navItem('space', this._emphasizeFirstLetter(t('domain.space.label'), t('domain.space.name')), { gap: 'gap-xs', extraClass: 'domain-link', icon: 'space-icon.png', srLabel: t('domain.space.label') }) : html``}

                    ${this._navItem('user', t('nav.account'), { gap: 'gap-sm' })}

                    ${this.user_profile && this.user_profile.paid && this.user_profile.groups && this.group_reports && this.group_reports.length > 0
                        ? this._navItem('teams', t('nav.teamProfile'))
                        : html``}

                    ${(hasRole('admin', this.user_profile) || hasRole('trainer', this.user_profile) || hasRole('program_manager', this.user_profile)) ? html`
                        <li class="gap-sm section-label" aria-hidden="true">${t('nav.trainerCertification')}</li>
                        ${(hasRole('admin', this.user_profile) || hasRole('trainer', this.user_profile)) ? html`
                            <li class="gap-xs">
                                <button type="button" class="unstyled-button selection domain-link" @click=${this.connectToMoodle}><img class="nav-icon" src="./images/training-icon.svg" alt="" aria-hidden="true" />${t('nav.training')}</button>
                            </li>
                        ` : html``}
                        ${(hasRole('trainer', this.user_profile) || hasRole('program_manager', this.user_profile))
                            ? this._navItem('training-docs', t('nav.trainingDocs'), { gap: 'gap-xs', extraClass: 'domain-link', icon: 'documents-icon.svg' })
                            : html``}
                    `: html``}

                    ${isStaff ? html`<li class="gap-sm section-label" aria-hidden="true">${t('nav.adminOperations')}</li>` : html``}
                    ${isStaff ? this._navItem('admin', t('nav.administration'), {
                        gap: 'gap-xs',
                        extraClass: 'domain-link',
                        icon: 'admin-icon.svg',
                        onSelect: () => {
                            emitEvent(INTRIVITY_NAV_SELECT, { "selection": "admin" }, this);
                            if (hasRole('program_manager', this.user_profile)) {
                                emitEvent(INTRIVITY_LOAD_PROGRAM_MANAGER,{},this);
                            }
                            if (hasRole('client_manager', this.user_profile)) {
                                emitEvent(INTRIVITY_LOAD_CLIENT_MANAGER,{},this);
                            }
                            if (hasRole('trainer', this.user_profile)) {
                                emitEvent(INTRIVITY_LOAD_TRAINER,{},this);
                            }
                            if (hasRole('admin', this.user_profile)) {
                                emitEvent(INTRIVITY_LOAD_CLIENTS,{},this);
                            }
                        }
                    }) : html``}

                    ${paidWithReport && (this.user_profile.paid || this.masqueraded_user) ? html`
                        <li class="gap-sm">
                            <button type="button" class="unstyled-button selection" @click=${(e) => {
                                emitEvent(INTRIVITY_PRINTABLE_TOGGLE, {}, this);
                                announce(t('nav.printableOpened'));
                            }}><img class="nav-icon" src="./images/download-icon.svg" alt="" aria-hidden="true" />${t('nav.downloadReport')}</button>
                        </li>
                    `: html``}

                    ${isStaff ? this._navItem('roles', t('nav.roleGuide'), {
                        extraClass: 'roles-guide',
                        onSelect: () => {
                            emitEvent(INTRIVITY_NAV_SELECT, { "selection": "roles" }, this);
                            window.history.pushState({}, '', '/roles');
                        }
                    }) : html``}

                `: html``}

                ${this._navItem('accessibility', t('nav.accessibility'), {
                    gap: 'gap-none',
                    extraClass: 'accessibility-link',
                    onSelect: () => {
                        emitEvent(INTRIVITY_NAV_SELECT, { "selection": "accessibility" }, this);
                        window.history.pushState({}, '', '/accessibility');
                    }
                })}

                ${this._navItem('privacy', t('nav.privacy'), {
                    gap: 'gap-none',
                    extraClass: 'privacy-link',
                    onSelect: () => {
                        emitEvent(INTRIVITY_NAV_SELECT, { "selection": "privacy" }, this);
                        window.history.pushState({}, '', '/privacy');
                    }
                })}
            </ul>
        `;
    }

    _renderMobileMenu() {
        return html`
            <button
                type="button"
                class="unstyled-button mobile-bar"
                aria-expanded=${this._mobileMenuOpen ? 'true' : 'false'}
                aria-controls="mobile-menu"
                @click=${() => { this._mobileMenuOpen = !this._mobileMenuOpen; }}>
                <span aria-hidden="true">&#9776;&nbsp;</span> ${t('nav.menu')}
            </button>
            ${this._mobileMenuOpen ? html`
                <div id="mobile-menu" class="mobile-menu" @click=${() => { this._mobileMenuOpen = false; }}>
                    ${this._renderMenuContent()}
                </div>
            `: html``}
        `;
    }

    render() {
        return html`
            <nav class="nav ${this.horizontal ? 'mobile' : ''}" aria-label=${t('nav.mainLabel')}>
                ${this.horizontal ? this._renderMobileMenu() : this._renderMenuContent()}
            </nav>
        `
    } 
 
}
if (!customElements.get('intrivity-nav')) customElements.define('intrivity-nav', IntrivityNavApp);
